import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, X, ChevronDown } from 'lucide-react';
import { useTranslation } from '../context/LanguageContext';

const SupplierSelect = ({ value, onChange, placeholder: placeholderProp, className = '' }) => {
  const { t } = useTranslation();
  const placeholder = placeholderProp || t('common.search_supplier_placeholder');
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);
  const debounceRef = useRef(null);

  // Load all suppliers on mount
  useEffect(() => {
    setLoading(true);
    fetch(`/api/suppliers?limit=200`)
      .then(res => res.json())
      .then(data => {
        if (data.status === 'success') {
          setSuppliers(data.data || []);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  // Debounced search
  const handleSearch = useCallback((term) => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setLoading(true);
      const params = new URLSearchParams({ limit: '200' });
      if (term.trim()) params.set('search', term.trim());
      fetch(`/api/suppliers?${params.toString()}`)
        .then(res => res.json())
        .then(data => {
          if (data.status === 'success') {
            setSuppliers(data.data || []);
          }
        })
        .catch(console.error)
        .finally(() => setLoading(false));
    }, 300);
  }, []);

  // Set selected supplier when value prop changes
  useEffect(() => {
    if (!value && value !== 0) {
      setSelectedSupplier(null);
      return;
    }
    const found = suppliers.find(s => String(s.entity_id) === String(value));
    if (found) setSelectedSupplier(found);
  }, [value, suppliers]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Safely get supplier display name
  const getSupplierName = (supplier) => {
    return supplier.attributes?.company_name || supplier.email || `#${supplier.entity_id}`;
  };

  const handleSelect = (supplier) => {
    setSelectedSupplier(supplier);
    setIsOpen(false);
    setSearch('');
    onChange(String(supplier.entity_id));
  };

  const handleClear = () => {
    setSelectedSupplier(null);
    setSearch('');
    onChange('');
  };

  const toggleOpen = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
  };

  const filteredSuppliers = search.trim()
    ? suppliers.filter(s => {
        const name = (s.attributes?.company_name || '').toLowerCase();
        const email = (s.email || '').toLowerCase();
        const term = search.toLowerCase();
        return name.includes(term) || email.includes(term);
      })
    : suppliers;

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      {/* Trigger button */}
      <div
        className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm cursor-pointer flex items-center justify-between hover:border-slate-400 dark:hover:border-slate-500 transition-colors bg-white dark:bg-slate-800"
        onClick={toggleOpen}
      >
        {selectedSupplier ? (
          <span className="text-slate-800 dark:text-white truncate">
            {getSupplierName(selectedSupplier)}
            {selectedSupplier.email && (
              <span className="text-slate-400 ml-1 text-xs">({selectedSupplier.email})</span>
            )}
          </span>
        ) : (
          <span className="text-slate-400">{placeholder}</span>
        )}
        <div className="flex items-center gap-1">
          {selectedSupplier && (
            <X
              size={14}
              className="text-slate-400 hover:text-red-500 cursor-pointer"
              onClick={(e) => { e.stopPropagation(); handleClear(); }}
            />
          )}
          <ChevronDown size={14} className={`text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute left-0 right-0 top-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl z-[9999] max-h-[300px] flex flex-col">
          {/* Search box */}
          <div className="p-2 border-b border-slate-100 dark:border-slate-700">
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={search}
                onChange={(e) => { setSearch(e.target.value); handleSearch(e.target.value); }}
                placeholder={t("common.search_supplier_placeholder")}
                className="w-full pl-8 pr-3 py-1.5 text-sm border border-slate-200 dark:border-slate-600 rounded-md outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-700 text-slate-800 dark:text-white"
              />
            </div>
          </div>

          {/* Options list */}
          <div className="overflow-y-auto flex-1">
            {loading ? (
              <div className="p-4 text-center text-sm text-slate-400">{t("common.loading")}</div>
            ) : filteredSuppliers.length === 0 ? (
              <div className="p-4 text-center text-sm text-slate-400">{t("common.no_matching_suppliers")}</div>
            ) : (
              filteredSuppliers.map(supplier => {
                const isSelected = selectedSupplier && String(supplier.entity_id) === String(selectedSupplier.entity_id);
                return (
                  <div
                    key={supplier.entity_id}
                    className={`px-3 py-2 text-sm cursor-pointer flex items-center justify-between hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors ${
                      isSelected ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400' : 'text-slate-700 dark:text-slate-300'
                    }`}
                    onClick={() => handleSelect(supplier)}
                  >
                    <span className="font-medium">{getSupplierName(supplier)}</span>
                    {supplier.email && (
                      <span className="text-xs text-slate-400 ml-2">{supplier.email}</span>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SupplierSelect;
